import React, { useState, useEffect } from "react";

interface PincodeInputProps {
  pincode: string;
  city: string;
  state: string;
  onChange: (field: string, value: string) => void;
  error?: string;
  cityError?: string;
  stateError?: string;
  onErrorChange: (error: string) => void;
  onFieldBlur?: (field: string) => void;
}

export const PincodeInput: React.FC<PincodeInputProps> = ({
  pincode,
  city,
  state,
  onChange,
  error,
  cityError,
  stateError,
  onErrorChange,
  onFieldBlur,
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Tier 2: Check invalid prefixes
  const isInvalidPrefix = (pin: string) => {
    const prefix = pin.substring(0, 2);
    const invalidPrefixes = [
      "10", "29", "90", "91", "92", "93", "94", "95", "96", "97", "98", "99"
    ];
    return invalidPrefixes.includes(prefix) || prefix.startsWith("0");
  };

  const verifyPincodeAPI = async (pin: string) => {
    setIsVerifying(true);
    onErrorChange("");
    setIsSuccess(false);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      
      if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice.length > 0) {
        const postOffice = data[0].PostOffice[0];
        const district = postOffice.District || postOffice.Block || postOffice.Name || "";
        const stateName = postOffice.State || "";
        
        onChange("city", district);
        onChange("state", stateName);
        setIsSuccess(true);
      } else {
        onChange("city", "");
        onChange("state", "");
        onErrorChange("We couldn't find this Pincode in the postal database. Please try again.");
      }
    } catch (err) {
      onChange("city", "");
      onChange("state", "");
      onErrorChange("We couldn't find this Pincode in the postal database. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    
    // Tier 1: Strict structural & format validation
    const numericVal = val.replace(/\D/g, "");
    const truncatedVal = numericVal.substring(0, 6);
    
    onChange("pincode", truncatedVal);
    
    if (truncatedVal.length > 0 && truncatedVal.startsWith("0")) {
      onChange("pincode", ""); // Prevent 0 as first char
      return;
    }

    if (truncatedVal.length === 6) {
      // Tier 2: Regional check
      if (isInvalidPrefix(truncatedVal)) {
        onErrorChange("This Pincode prefix is not recognized. Please check for typos.");
        onChange("city", "");
        onChange("state", "");
        setIsSuccess(false);
      } else {
        // Tier 3: Live Verification
        verifyPincodeAPI(truncatedVal);
      }
    } else {
      setIsSuccess(false);
      onErrorChange("");
      onChange("city", "");
      onChange("state", "");
    }
  };

  const handleBlur = () => {
    if (pincode && pincode.length !== 6) {
      onErrorChange("Please enter a valid 6-digit Pincode.");
    }
    if (onFieldBlur) {
      onFieldBlur("pincode");
    }
  };

  return (
    <div className="w-full space-y-4">
      <div>
        <div className="flex justify-between">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
            PIN Code*
          </label>
          {isVerifying && (
            <span className="text-xs text-blue-500 font-medium animate-pulse">Verifying...</span>
          )}
        </div>
        <input
          type="text"
          required
          maxLength={6}
          placeholder="e.g. 110001"
          aria-label="PIN Code"
          value={pincode}
          onChange={handlePincodeChange}
          onBlur={handleBlur}
          className={`w-full bg-[#f8f8f8] border ${error ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400`}
        />
        {error && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{error}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
            City*
          </label>
          <input
            type="text"
            required
            disabled
            placeholder="e.g. New Delhi"
            aria-label="City"
            value={city}
            className={`w-full bg-[#f8f8f8] border ${cityError ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400 opacity-60 cursor-not-allowed`}
          />
          {cityError && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{cityError}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
            State*
          </label>
          <input
            type="text"
            required
            disabled
            placeholder="e.g. Delhi"
            aria-label="State"
            value={state}
            className={`w-full bg-[#f8f8f8] border ${stateError ? "border-red-500" : "border-transparent"} focus:border-black focus:bg-white px-4 py-3 text-[15px] outline-none transition-all rounded-xl placeholder-gray-400 opacity-60 cursor-not-allowed`}
          />
          {stateError && <p className="text-red-500 text-[11px] mt-1.5 font-medium">{stateError}</p>}
        </div>
      </div>
    </div>
  );
};
